import React, { useMemo, useState } from "react";
import styled from "styled-components/macro";
import { Helmet } from "react-helmet-async";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Breadcrumbs as MuiBreadcrumbs,
  Button,
  Divider as MuiDivider,
  Link,
  Paper,
  Typography as MuiTypography,
} from "@material-ui/core";
import { spacing } from "@material-ui/system";
import MaterialTable, { MTableToolbar } from "material-table";
import CopyIcon from "@material-ui/icons/FileCopy";
import { ExpandMore } from "@material-ui/icons";

import { NavLink } from "react-router-dom";
import { useApp } from "../../../../AppProvider";

import { copyToClipboard, dateFormatter } from "../../../../utils";
import { WATER_QUALITY_PARAMETERS_TABLE_COLUMNS } from "./WaterQualityParametersConstants";
import BasicWaterQualityParametersInfo from "./BasicWaterQualityParametersInfo";
import { useWaterQualityParameters } from "./useWaterQualityParameters";

const Divider = styled(MuiDivider)(spacing);
const Breadcrumbs = styled(MuiBreadcrumbs)(spacing);
const Typography = styled(MuiTypography)(spacing);

const TableWrapper = styled.div`
  overflow-y: auto;
  max-width: calc(100vw - ${(props) => props.theme.spacing(12)}px);
  height: 100%;
  width: 100%;
`;

function WaterQualityParametersManagement() {
  const {
    selectedWaterQualityParameter,
    setSelectedWaterQualityParameter,
    data,
    isFetching,
    error,
    refetch,
  } = useWaterQualityParameters();
  const { doToast } = useApp();

  const [showIgnored, setShowIgnored] = useState(false);

  const filteredData = useMemo(() => {
    if (!Array.isArray(data)) return [];
    const filtered = showIgnored ? data : data.filter((row) => !row.ignore);
    return filtered.map((row) => ({ ...row })); // <-- clone objects
  }, [data, showIgnored]);

  const columns = useMemo(() => {
    return WATER_QUALITY_PARAMETERS_TABLE_COLUMNS.map((c) =>
      c.field === "ignore" ? { ...c, filtering: false } : { ...c }
    );
  }, [showIgnored]); //eslint-disable-line react-hooks/exhaustive-deps

  if (error) return "An error has occurred: " + error.message;

  return (
    <React.Fragment>
      <Helmet title="Edit Water Quality Parameters" />
      <Typography variant="h3" gutterBottom display="inline">
        Edit Water Quality Parameters
      </Typography>

      <Breadcrumbs aria-label="Breadcrumb" mt={2}>
        <Link component={NavLink} exact to="/dashboard">
          Dashboard
        </Link>
        <Typography>Water Quality Parameters</Typography>
      </Breadcrumbs>

      <Divider my={6} />

      <Box>
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Box style={{ width: "100%" }}>
              <Typography variant="h3">
                Water Quality Parameters Selection
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <TableWrapper>
              <MaterialTable
                key={showIgnored ? "with-ignored" : "without-ignored"}
                title={`Water Quality Parameters Setup ${dateFormatter(
                  new Date(),
                  "MM/DD/YYYY, h:mm A"
                )}`}
                loading={isFetching || !data}
                columns={columns}
                data={filteredData}
                localization={{
                  toolbar: {
                    searchPlaceholder: "Water Quality Parameters Selection",
                  },
                }}
                actions={[
                  {
                    icon: CopyIcon,
                    tooltip: "Copy Data",
                    isFreeAction: true,
                    onClick: () => {
                      try {
                        copyToClipboard(
                          filteredData,
                          WATER_QUALITY_PARAMETERS_TABLE_COLUMNS,
                          () =>
                            doToast(
                              "success",
                              "Data was copied to your clipboard."
                            )
                        );
                      } catch (error) {
                        const message =
                          error?.message ?? "Something went wrong";
                        doToast("error", message);
                      }
                    },
                  },
                  () => ({
                    icon: "edit",
                    tooltip: "Edit",
                    onClick: (event, rowData) => {
                      setSelectedWaterQualityParameter(
                        rowData?.aqsci_parameter_ndx
                      );
                    },
                  }),
                  {
                    icon: "add_box",
                    tooltip: "Add",
                    isFreeAction: true,
                    onClick: () => {
                      setSelectedWaterQualityParameter(0);
                    },
                  },
                ]}
                options={{
                  filtering: true,
                  emptyRowsWhenPaging: false,
                  showTitle: false,
                  columnsButton: true,
                  exportButton: true,
                  exportAllData: true,
                  addRowPosition: "first",
                  pageSize: 10,
                  pageSizeOptions: [10, 30, 50, 100, 200],
                  padding: "dense",
                  searchFieldAlignment: "left",
                  minBodyHeight: "250px",
                  maxBodyHeight:
                    "calc(100vh - 64px - 48px - 106px - 48px - 64px - 204px - 100px)",
                }}
                components={{
                  Toolbar: (props) => (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        flexWrap: "wrap",
                        paddingRight: "16px",
                      }}
                    >
                      <div style={{ flexGrow: 1, minWidth: 0 }}>
                        <MTableToolbar {...props} />
                      </div>
                      <Button
                        variant={showIgnored ? "contained" : "outlined"}
                        color="secondary"
                        size="small"
                        onClick={() => setShowIgnored((prev) => !prev)}
                        style={{
                          marginLeft: 16,
                          whiteSpace: "nowrap",
                          textTransform: "none",
                          width: "100px",
                        }}
                      >
                        {showIgnored ? "Hide Ignored" : "Show Ignored"}
                      </Button>
                    </div>
                  ),
                }}
              />
            </TableWrapper>
          </AccordionDetails>
        </Accordion>

        <Box
          style={{
            display:
              selectedWaterQualityParameter ||
              selectedWaterQualityParameter === 0
                ? "block"
                : "none",
          }}
        >
          <Paper style={{ margin: "16px 0" }}>
            <Box
              p={4}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Typography variant="h4">
                {selectedWaterQualityParameter === 0
                  ? "Creating New Water Quality Parameter"
                  : "Editing Existing Water Quality Parameter"}
              </Typography>
            </Box>
          </Paper>
          <BasicWaterQualityParametersInfo
            selectedWaterQualityParameter={selectedWaterQualityParameter}
            setSelectedWaterQualityParameter={setSelectedWaterQualityParameter}
            refetchWaterQualityParameters={refetch}
          />
          <Paper style={{ margin: "16px 0" }}>
            <Box
              p={4}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Typography variant="h4">
                {selectedWaterQualityParameter === 0
                  ? "Creating New Water Quality Parameter"
                  : "Editing Existing Water Quality Parameter"}
              </Typography>
              <Button
                variant="contained"
                color="primary"
                onClick={() => setSelectedWaterQualityParameter(null)}
              >
                Cancel
              </Button>
            </Box>
          </Paper>
        </Box>
      </Box>
    </React.Fragment>
  );
}

export default WaterQualityParametersManagement;
